import React, { useEffect, useMemo, useState } from 'react';
import { CountryCodeInput } from '../../../shared/components/CountryFlag';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Card,
    Row,
    Col,
    Space,
    Tag,
    Button,
    Descriptions,
    Tabs,
    Table,
    Form,
    Input,
    Switch,
    message,
    Divider,
    Typography,
    Select,
    Spin,
    Tooltip,
    Modal,
    Popconfirm,
    Alert
} from 'antd';
import {
    ArrowLeftOutlined,
    ReloadOutlined,
    LockOutlined,
    UnlockOutlined,
    SafetyOutlined,
    KeyOutlined,
    SyncOutlined,
    PlusOutlined,
    EditOutlined,
    DeleteOutlined,
    CloudServerOutlined,
    SecurityScanOutlined,
    HistoryOutlined,
    CheckCircleOutlined,
    StopOutlined
} from '@ant-design/icons';
import moment from 'moment';
import { registrarService } from '../../services/registrarService';
import { CONTACT_ROLES } from '../../../shared/constants/contactRoles';
import PrivacyModePanel from '../../components/domain/PrivacyModePanel';

const { Text, Title } = Typography;
const { Option } = Select;

const createEmptyContactValues = () => ({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    postalCode: '',
    country: ''
});

const ensureContactStructure = (contacts = {}) => {
    const structured = {};
    const hasRoleData = CONTACT_ROLES.some(role => contacts && typeof contacts === 'object' && contacts[role.key]);

    CONTACT_ROLES.forEach(role => {
        if (hasRoleData) {
            structured[role.key] = {
                ...createEmptyContactValues(),
                ...(contacts[role.key] || {})
            };
        } else {
            structured[role.key] = {
                ...createEmptyContactValues(),
                ...(contacts || {})
            };
        }
    });

    return structured;
};

const createRoleTemplateState = () =>
    CONTACT_ROLES.reduce((acc, role) => {
        acc[role.key] = null;
        return acc;
    }, {});

// DNSSEC algorithm options
const DNSSEC_ALGORITHMS = [
    { value: 5, label: '5 - RSA/SHA-1' },
    { value: 7, label: '7 - RSASHA1-NSEC3-SHA1' },
    { value: 8, label: '8 - RSA/SHA-256' },
    { value: 10, label: '10 - RSA/SHA-512' },
    { value: 13, label: '13 - ECDSA Curve P-256 with SHA-256' },
    { value: 14, label: '14 - ECDSA Curve P-384 with SHA-384' },
    { value: 15, label: '15 - Ed25519' },
    { value: 16, label: '16 - Ed448' },
];

const DNSSEC_DIGEST_TYPES = [
    { value: 1, label: '1 - SHA-1' },
    { value: 2, label: '2 - SHA-256' },
    { value: 3, label: '3 - GOST R 34.11-94' },
    { value: 4, label: '4 - SHA-384' },
];

const DomainDetail = () => {
    const { domainId } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [domain, setDomain] = useState(null);
    const [contactTemplates, setContactTemplates] = useState([]);
    const [templatesLoading, setTemplatesLoading] = useState(false);
    const [roleTemplateIds, setRoleTemplateIds] = useState(() => createRoleTemplateState());
    const [bulkTemplateId, setBulkTemplateId] = useState(null);
    const [nsSubmitting, setNsSubmitting] = useState(false);
    const [contactsSubmitting, setContactsSubmitting] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);
    const [authCodeModalVisible, setAuthCodeModalVisible] = useState(false);
    const [authCode, setAuthCode] = useState('');

    // Glue Records state
    const [glueRecords, setGlueRecords] = useState([]);
    const [glueLoading, setGlueLoading] = useState(false);
    const [glueSubmitting, setGlueSubmitting] = useState(false);
    const [glueForm] = Form.useForm();
    const [glueEditForm] = Form.useForm();
    const [editingGlue, setEditingGlue] = useState(null);

    // DNSSEC state
    const [dnssecRecords, setDnssecRecords] = useState([]);
    const [dnssecLoading, setDnssecLoading] = useState(false);
    const [dnssecSubmitting, setDnssecSubmitting] = useState(false);
    const [dnssecForm] = Form.useForm();

    // Sync logs state
    const [syncLogs, setSyncLogs] = useState([]);
    const [syncLogsLoading, setSyncLogsLoading] = useState(false);
    const [syncLogsTotal, setSyncLogsTotal] = useState(0);
    const [syncLogsPage, setSyncLogsPage] = useState(1);
    const [syncTriggering, setSyncTriggering] = useState(false);

    const [nsForm] = Form.useForm();
    const [contactsForm] = Form.useForm();

    const statusTag = useMemo(() => {
        const status = domain?.status;
        const map = {
            pending: { color: 'processing', text: '待激活' },
            active: { color: 'success', text: '正常' },
            expired: { color: 'error', text: '已过期' },
            suspended: { color: 'warning', text: '已暂停' },
            transferred: { color: 'default', text: '已转出' },
            cancelled: { color: 'default', text: '已取消' }
        };
        const config = map[status] || { color: 'default', text: status || '未知' };
        return <Tag color={config.color}>{config.text}</Tag>;
    }, [domain]);

    const contactTemplateOptions = contactTemplates.map(template => ({
        label: template.template_name,
        value: template.id,
        entity: template
    }));

    useEffect(() => {
        loadDomainDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [domainId]);

    const loadContactTemplates = async (ownerUserId) => {
        setTemplatesLoading(true);
        try {
            const response = await registrarService.getContactTemplates(ownerUserId);
            if (response.success) {
                setContactTemplates(response.data || []);
            }
        } catch (e) {
            // ignore
        } finally {
            setTemplatesLoading(false);
        }
    };

    const loadDomainDetail = async () => {
        setLoading(true);
        try {
            const response = await registrarService.getDomainDetail(domainId);
            if (response.success) {
                const data = response.data;
                setDomain(data);
                loadContactTemplates(data.user_id);
                const ns = Array.isArray(data.nameservers) ? data.nameservers : [];
                nsForm.setFieldsValue({ nameservers: ns.join('\n') });

                const contacts = ensureContactStructure(data.contacts || {});
                contactsForm.setFieldsValue({ contacts });
                const templateIds = data.contact_template_ids || contacts._template_ids || {};
                setRoleTemplateIds({
                    ...createRoleTemplateState(),
                    ...templateIds
                });
                setBulkTemplateId(null);
            } else {
                message.error(response.error || '加载域名详情失败');
            }
        } catch (error) {
            message.error(error.message || '加载域名详情失败');
        } finally {
            setLoading(false);
        }
    };

    const refresh = () => loadDomainDetail();

    // Glue Records functions
    const loadGlueRecords = async () => {
        if (!domain) return;
        setGlueLoading(true);
        try {
            const response = await registrarService.getGlueRecords(domain.id);
            if (response.success) {
                setGlueRecords(response.data?.glue_records || []);
            } else {
                message.error(response.error || '加载胶水记录失败');
            }
        } catch (error) {
            message.error(error.message || '加载胶水记录失败');
        } finally {
            setGlueLoading(false);
        }
    };

    const handleAddGlueRecord = async () => {
        try {
            const values = await glueForm.validateFields();
            setGlueSubmitting(true);
            const response = await registrarService.addGlueRecord(
                domain.id,
                values.hostname,
                values.ipv4,
                values.ipv6 || null
            );
            if (response.success) {
                message.success('胶水记录添加成功');
                glueForm.resetFields();
                loadGlueRecords();
            } else {
                message.error(response.error || '添加失败');
            }
        } catch (error) {
            if (!error.errorFields) {
                message.error(error.message || '添加失败');
            }
        } finally {
            setGlueSubmitting(false);
        }
    };

    const handleUpdateGlueRecord = async () => {
        try {
            const values = await glueEditForm.validateFields();
            setGlueSubmitting(true);
            const response = await registrarService.updateGlueRecord(
                domain.id,
                editingGlue.hostname,
                values.ipv4,
                values.ipv6 || null
            );
            if (response.success) {
                message.success('胶水记录更新成功');
                setEditingGlue(null);
                loadGlueRecords();
            } else {
                message.error(response.error || '更新失败');
            }
        } catch (error) {
            if (!error.errorFields) {
                message.error(error.error || error.message || '更新失败');
            }
        } finally {
            setGlueSubmitting(false);
        }
    };

    const handleDeleteGlueRecord = async (hostname) => {
        try {
            const response = await registrarService.deleteGlueRecord(domain.id, hostname);
            if (response.success) {
                message.success('胶水记录删除成功');
                loadGlueRecords();
            } else {
                message.error(response.error || '删除失败');
            }
        } catch (error) {
            message.error(error.message || '删除失败');
        }
    };

    // DNSSEC functions
    const loadDnssecRecords = async () => {
        if (!domain) return;
        setDnssecLoading(true);
        try {
            const response = await registrarService.getDnssec(domain.id);
            if (response.success) {
                setDnssecRecords(response.data?.dnssec_data || []);
            } else {
                message.error(response.error || '加载DNSSEC记录失败');
            }
        } catch (error) {
            message.error(error.message || '加载DNSSEC记录失败');
        } finally {
            setDnssecLoading(false);
        }
    };

    const handleAddDnssec = async () => {
        try {
            const values = await dnssecForm.validateFields();
            setDnssecSubmitting(true);
            const response = await registrarService.addDnssec(domain.id, {
                key_tag: values.key_tag,
                algorithm: values.algorithm,
                digest_type: values.digest_type,
                digest: values.digest
            });
            if (response.success) {
                message.success('DNSSEC记录添加成功');
                dnssecForm.resetFields();
                loadDnssecRecords();
            } else {
                message.error(response.error || '添加失败');
            }
        } catch (error) {
            if (!error.errorFields) {
                message.error(error.message || '添加失败');
            }
        } finally {
            setDnssecSubmitting(false);
        }
    };

    const handleDeleteDnssec = async (index) => {
        try {
            const response = await registrarService.deleteDnssec(domain.id, index);
            if (response.success) {
                message.success('DNSSEC记录删除成功');
                loadDnssecRecords();
            } else {
                message.error(response.error || '删除失败');
            }
        } catch (error) {
            message.error(error.message || '删除失败');
        }
    };

    // Sync logs functions
    const loadSyncLogs = async (page = 1) => {
        if (!domain) return;
        setSyncLogsLoading(true);
        try {
            const response = await registrarService.getDomainSyncLogs(domain.id, { page, limit: 20 });
            if (response.code === 0 || response.success) {
                setSyncLogs(response.data?.logs || []);
                setSyncLogsTotal(response.data?.total || 0);
                setSyncLogsPage(page);
            } else {
                message.error(response.message || response.error || '加载同步日志失败');
            }
        } catch (error) {
            message.error(error.message || '加载同步日志失败');
        } finally {
            setSyncLogsLoading(false);
        }
    };

    const handleTriggerSync = async () => {
        if (!domain) return;
        setSyncTriggering(true);
        try {
            const response = await registrarService.triggerDomainSync(domain.id);
            if (response.code === 0 || response.success) {
                message.success('已触发同步，稍后刷新查看结果');
                loadSyncLogs(1);
            } else {
                message.error(response.message || response.error || '触发同步失败');
            }
        } catch (error) {
            message.error(error.message || '触发同步失败');
        } finally {
            setSyncTriggering(false);
        }
    };

    const handleToggleLock = async () => {
        if (!domain) return;
        setActionLoading(true);
        try {
            const response = await registrarService.toggleDomainLock(domain.id, !domain.domain_lock);
            if (response.success) {
                message.success('已提交到注册商');
                refresh();
            } else {
                message.error(response.error || '操作失败');
            }
        } catch (error) {
            message.error(error.error || error.message || '操作失败');
        } finally {
            setActionLoading(false);
        }
    };

    const handleTogglePrivacy = async () => {
        if (!domain) return;
        setActionLoading(true);
        try {
            const response = await registrarService.toggleWhoisPrivacy(domain.id, !domain.whois_privacy);
            if (response.success) {
                message.success('已提交到注册商');
                refresh();
            } else {
                message.error(response.error || '操作失败');
            }
        } catch (error) {
            message.error(error.error || error.message || '操作失败');
        } finally {
            setActionLoading(false);
        }
    };

    const handleToggleAutoRenew = async () => {
        if (!domain) return;
        setActionLoading(true);
        try {
            const response = await registrarService.toggleAutoRenew(domain.id, !domain.auto_renew);
            if (response.success) {
                message.success(response.auto_renew ? '自动续费已开启' : '自动续费已关闭');
                refresh();
            } else {
                message.error(response.error || '操作失败');
            }
        } catch (error) {
            message.error(error.error || error.message || '操作失败');
        } finally {
            setActionLoading(false);
        }
    };

    const handleGetAuthCode = async () => {
        if (!domain) return;
        try {
            const response = await registrarService.getAuthCode(domain.id);
            if (response.success) {
                setAuthCode(response.auth_code);
                setAuthCodeModalVisible(true);
            } else {
                message.error(response.error || '获取转移码失败');
            }
        } catch (error) {
            message.error(error.message || '获取转移码失败');
        }
    };

    const handleNameserverSubmit = async () => {
        if (!domain) return;
        try {
            const values = await nsForm.validateFields();
            const nameservers = (values.nameservers || '')
                .split(/\s+/)
                .map(ns => ns.trim())
                .filter(Boolean);
            if (nameservers.length < 2) {
                message.warning('请至少填写2个NS');
                return;
            }
            setNsSubmitting(true);
            const response = await registrarService.updateNameservers(domain.id, nameservers);
            if (response.success) {
                message.success('已提交到注册商');
                refresh();
            } else {
                message.error(response.error || '更新失败');
            }
        } catch (error) {
            if (!error.errorFields) {
                message.error(error.error || error.message || '更新失败');
            }
        } finally {
            setNsSubmitting(false);
        }
    };

    const fillContactFromTemplate = (roleKey, template) => {
        if (!template) return;
        const current = contactsForm.getFieldValue('contacts') || {};
        contactsForm.setFieldsValue({
            contacts: {
                ...current,
                [roleKey]: {
                    ...createEmptyContactValues(),
                    firstName: template.first_name,
                    lastName: template.last_name,
                    email: template.email,
                    phone: template.phone,
                    address: template.address,
                    city: template.city,
                    state: template.state,
                    postalCode: template.postal_code,
                    country: template.country ? template.country.toUpperCase() : ''
                }
            }
        });
    };

    const handleRoleTemplateChange = (roleKey, templateId) => {
        setRoleTemplateIds(prev => ({
            ...prev,
            [roleKey]: templateId || null
        }));
        if (!templateId) return;
        const template = contactTemplates.find(t => t.id === templateId);
        fillContactFromTemplate(roleKey, template);
    };

    const handleApplyTemplateToAll = (templateId) => {
        if (!templateId) {
            setRoleTemplateIds(createRoleTemplateState());
            setBulkTemplateId(null);
            return;
        }
        const template = contactTemplates.find(t => t.id === templateId);
        if (!template) return;
        const nextState = createRoleTemplateState();
        CONTACT_ROLES.forEach(role => {
            nextState[role.key] = templateId;
            fillContactFromTemplate(role.key, template);
        });
        setRoleTemplateIds(nextState);
        setBulkTemplateId(templateId);
    };

    const syncContactsFromRegistrant = () => {
        const values = contactsForm.getFieldValue(['contacts', 'registrant']);
        if (!values) {
            message.warning('请先填写注册人信息');
            return;
        }
        const updates = {};
        CONTACT_ROLES.filter(role => role.key !== 'registrant').forEach(role => {
            updates[role.key] = { ...values };
        });
        const current = contactsForm.getFieldValue('contacts') || {};
        contactsForm.setFieldsValue({
            contacts: {
                ...current,
                ...updates
            }
        });
        setRoleTemplateIds(prev => {
            const tplId = prev.registrant;
            if (!tplId) return prev;
            const next = { ...prev };
            CONTACT_ROLES.forEach(role => {
                if (role.key !== 'registrant') {
                    next[role.key] = tplId;
                }
            });
            return next;
        });
    };

    const handleContactsSubmit = async () => {
        if (!domain) return;
        try {
            const values = await contactsForm.validateFields();
            const contactValues = values.contacts || {};
            const payload = {};
            const templatePayload = {};
            CONTACT_ROLES.forEach(role => {
                const roleData = contactValues[role.key] || {};
                payload[role.key] = {
                    ...roleData,
                    country: roleData.country ? roleData.country.toUpperCase() : roleData.country
                };
                if (roleTemplateIds[role.key]) {
                    templatePayload[role.key] = roleTemplateIds[role.key];
                }
            });

            setContactsSubmitting(true);
            const response = await registrarService.updateContacts(domain.id, payload, templatePayload);
            if (response.success) {
                message.success('已提交到注册商');
                refresh();
            } else {
                message.error(response.error || '更新失败');
            }
        } catch (error) {
            if (!error.errorFields) {
                message.error(error.error || error.message || '更新失败');
            }
        } finally {
            setContactsSubmitting(false);
        }
    };

    const operationsColumns = [
        {
            title: '时间',
            dataIndex: 'created_at',
            render: (v) => (v ? moment(v).format('YYYY-MM-DD HH:mm') : '-')
        },
        { title: '操作', dataIndex: 'operation_type' },
        {
            title: '状态',
            dataIndex: 'status',
            render: (status) => {
                const map = {
                    pending: { color: 'processing', text: '待处理' },
                    processing: { color: 'processing', text: '处理中' },
                    completed: { color: 'success', text: '完成' },
                    failed: { color: 'error', text: '失败' }
                };
                const cfg = map[status] || { color: 'default', text: status };
                return <Tag color={cfg.color}>{cfg.text}</Tag>;
            }
        },
        {
            title: '备注',
            dataIndex: 'error_message',
            render: (msg, record) => msg || record.response_data || '-'
        }
    ];

    const glueColumns = [
        { title: '主机名', dataIndex: 'hostname', key: 'hostname' },
        { title: 'IPv4', dataIndex: 'ipv4', key: 'ipv4', render: (v) => v || '-' },
        { title: 'IPv6', dataIndex: 'ipv6', key: 'ipv6', render: (v) => v || '-' },
        {
            title: '操作',
            key: 'actions',
            width: 150,
            render: (_, record) => (
                <Space>
                    <Button
                        type="link"
                        size="small"
                        icon={<EditOutlined />}
                        onClick={() => {
                            setEditingGlue(record);
                            glueEditForm.setFieldsValue({
                                ipv4: record.ipv4,
                                ipv6: record.ipv6,
                            });
                        }}
                    >
                        编辑
                    </Button>
                    <Popconfirm
                        title="确定要删除这条胶水记录吗？"
                        onConfirm={() => handleDeleteGlueRecord(record.hostname)}
                        okText="确定"
                        cancelText="取消"
                    >
                        <Button type="link" size="small" danger icon={<DeleteOutlined />}>
                            删除
                        </Button>
                    </Popconfirm>
                </Space>
            )
        }
    ];

    const dnssecColumns = [
        { title: 'Key Tag', dataIndex: 'key_tag', key: 'key_tag', width: 100 },
        {
            title: 'Algorithm',
            dataIndex: 'algorithm',
            key: 'algorithm',
            width: 80,
            render: (v) => {
                const algo = DNSSEC_ALGORITHMS.find(a => a.value === v);
                return algo ? algo.label : v;
            }
        },
        {
            title: 'Digest Type',
            dataIndex: 'digest_type',
            key: 'digest_type',
            width: 100,
            render: (v) => {
                const dt = DNSSEC_DIGEST_TYPES.find(d => d.value === v);
                return dt ? dt.label : v;
            }
        },
        {
            title: 'Digest',
            dataIndex: 'digest',
            key: 'digest',
            ellipsis: true,
            render: (v) => <Text copyable={{ text: v }} code style={{ fontSize: 11 }}>{v}</Text>
        },
        {
            title: '操作',
            key: 'actions',
            width: 80,
            render: (_, record, index) => (
                <Popconfirm
                    title="确定要删除这条DNSSEC记录吗？"
                    onConfirm={() => handleDeleteDnssec(index)}
                    okText="确定"
                    cancelText="取消"
                >
                    <Button type="link" size="small" danger icon={<DeleteOutlined />}>
                        删除
                    </Button>
                </Popconfirm>
            )
        }
    ];

    const syncLogColumns = [
        {
            title: '时间',
            dataIndex: 'synced_at',
            key: 'synced_at',
            width: 160,
            render: (v, record) => {
                const t = v || record.created_at;
                return t ? moment(t).format('YYYY-MM-DD HH:mm:ss') : '-';
            }
        },
        {
            title: '类型',
            dataIndex: 'sync_type',
            key: 'sync_type',
            width: 100,
            render: (v) => v || '-'
        },
        {
            title: '状态',
            dataIndex: 'status',
            key: 'status',
            width: 100,
            render: (status, record) => {
                const value = status || record.sync_status;
                const map = {
                    success: { color: 'success', text: '成功' },
                    failed: { color: 'error', text: '失败' },
                    pending: { color: 'processing', text: '待处理' },
                    processing: { color: 'processing', text: '处理中' }
                };
                const cfg = map[value] || { color: 'default', text: value || '-' };
                return <Tag color={cfg.color}>{cfg.text}</Tag>;
            }
        },
        {
            title: '变更',
            dataIndex: 'changes_detected',
            key: 'changes_detected',
            render: (v) => {
                if (!v || (Array.isArray(v) && v.length === 0) || (typeof v === 'object' && Object.keys(v).length === 0)) {
                    return <Text type="secondary">无变更</Text>;
                }
                const text = typeof v === 'string' ? v : JSON.stringify(v);
                return (
                    <Tooltip title={text}>
                        <Text style={{ fontSize: 12 }} ellipsis code>{text}</Text>
                    </Tooltip>
                );
            }
        },
        {
            title: '错误信息',
            dataIndex: 'error_message',
            key: 'error_message',
            render: (v) => (v ? <Text type="danger" style={{ fontSize: 12 }}>{v}</Text> : '-')
        }
    ];

    const operations = domain?.operations || [];
    const nameservers = Array.isArray(domain?.nameservers) ? domain.nameservers : [];

    const handleTabChange = (activeKey) => {
        if (activeKey === 'glue' && domain) {
            loadGlueRecords();
        } else if (activeKey === 'dnssec' && domain) {
            loadDnssecRecords();
        } else if (activeKey === 'sync-logs' && domain) {
            loadSyncLogs(1);
        }
    };

    return (
        <Spin spinning={loading}>
            <Space direction="vertical" size={16} style={{ width: '100%' }}>
                <Space align="center">
                    <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>
                        返回
                    </Button>
                    <Button icon={<ReloadOutlined />} onClick={refresh}>
                        刷新
                    </Button>
                </Space>

                <Card>
                    <Row gutter={[16, 16]}>
                        <Col xs={24} md={16}>
                            <Space direction="vertical" size={8} style={{ width: '100%' }}>
                                <Title level={4} style={{ margin: 0 }}>
                                    {domain?.domain || '域名详情'}
                                </Title>
                                <Space wrap>
                                    {statusTag}
                                    {domain?.domain_lock ? (
                                        <Tag icon={<LockOutlined />} color="blue">
                                            已锁定
                                        </Tag>
                                    ) : (
                                        <Tag icon={<UnlockOutlined />} color="default">
                                            未锁定
                                        </Tag>
                                    )}
                                    {domain?.whois_privacy ? (
                                        <Tag icon={<SafetyOutlined />} color="green">
                                            隐私保护
                                        </Tag>
                                    ) : (
                                        <Tag color="default">隐私关闭</Tag>
                                    )}
                                    {domain?.auto_renew ? (
                                        <Tag icon={<CheckCircleOutlined />} color="cyan">
                                            自动续费开启
                                        </Tag>
                                    ) : (
                                        <Tag icon={<StopOutlined />} color="default">
                                            自动续费关闭
                                        </Tag>
                                    )}
                                </Space>
                                <Descriptions column={1} size="small">
                                    <Descriptions.Item label="用户">
                                        <Text>{domain?.username}</Text> <Text type="secondary">ID: {domain?.user_id}</Text>
                                    </Descriptions.Item>
                                    <Descriptions.Item label="注册商">{domain?.registrar_name || '-'}</Descriptions.Item>
                                    <Descriptions.Item label="到期时间">
                                        {domain?.expires_at ? moment(domain.expires_at).format('YYYY-MM-DD') : '-'}
                                    </Descriptions.Item>
                                    <Descriptions.Item label="创建时间">
                                        {domain?.created_at ? moment(domain.created_at).format('YYYY-MM-DD HH:mm') : '-'}
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Auth Code">
                                        <Space>
                                            <Text type="secondary">点击获取转移码</Text>
                                            <Button size="small" icon={<KeyOutlined />} onClick={handleGetAuthCode}>
                                                获取
                                            </Button>
                                        </Space>
                                    </Descriptions.Item>
                                </Descriptions>
                            </Space>
                        </Col>
                        <Col xs={24} md={8}>
                            <Space direction="vertical" style={{ width: '100%' }}>
                                <Button
                                    block
                                    icon={domain?.domain_lock ? <UnlockOutlined /> : <LockOutlined />}
                                    onClick={handleToggleLock}
                                    loading={actionLoading}
                                >
                                    {domain?.domain_lock ? '解锁域名' : '锁定域名'}
                                </Button>
                                <Button
                                    block
                                    icon={<SafetyOutlined />}
                                    onClick={handleTogglePrivacy}
                                    loading={actionLoading}
                                >
                                    {domain?.whois_privacy ? '关闭隐私保护' : '开启隐私保护'}
                                </Button>
                                <Button
                                    block
                                    icon={domain?.auto_renew ? <StopOutlined /> : <CheckCircleOutlined />}
                                    onClick={handleToggleAutoRenew}
                                    loading={actionLoading}
                                >
                                    {domain?.auto_renew ? '关闭自动续费' : '开启自动续费'}
                                </Button>
                                {(domain?.pending_admin_ops || []).length > 0 && (
                                    <Tooltip title="注册商处理中，完成后刷新可见">
                                        <Tag color="processing">处理中：{domain.pending_admin_ops.length}</Tag>
                                    </Tooltip>
                                )}
                            </Space>
                        </Col>
                    </Row>
                </Card>

                <Card bodyStyle={{ padding: 0 }}>
                    <Tabs
                        defaultActiveKey="overview"
                        onChange={handleTabChange}
                        items={[
                            {
                                key: 'overview',
                                label: '概览',
                                children: (
                                    <Space direction="vertical" style={{ width: '100%', padding: 16 }} size={16}>
                                        <Card type="inner" title="域名服务器">
                                            <Form form={nsForm} layout="vertical">
                                                <Form.Item
                                                    label="Nameservers（每行一个）"
                                                    name="nameservers"
                                                    rules={[{ required: true, message: '请填写域名服务器' }]}
                                                >
                                                    <Input.TextArea rows={4} placeholder="ns1.example.com&#10;ns2.example.com" />
                                                </Form.Item>
                                                <Space>
                                                    <Button type="primary" onClick={handleNameserverSubmit} loading={nsSubmitting}>
                                                        更新NS
                                                    </Button>
                                                    <Text type="secondary">当前：{nameservers.join(', ') || '未设置'}</Text>
                                                </Space>
                                            </Form>
                                        </Card>
                                        <Card type="inner" title="最近操作">
                                            <Table
                                                size="small"
                                                rowKey="id"
                                                columns={operationsColumns}
                                                dataSource={operations}
                                                pagination={false}
                                                scroll={{ x: true }}
                                                locale={{ emptyText: '暂无操作记录' }}
                                            />
                                        </Card>
                                    </Space>
                                )
                            },
                            {
                                key: 'contacts',
                                label: domain?.real_contacts_redacted ? <Space size={4}>联系人<Tag color="purple" style={{ marginInlineEnd: 0 }}>隐私模式</Tag></Space> : '联系人',
                                children: domain?.real_contacts_redacted ? <PrivacyModePanel domain={domain} /> : (
                                    <Space direction="vertical" style={{ width: '100%', padding: 16 }} size={16}>
                                        <Space wrap>
                                            <Select
                                                placeholder="套用模板到全部"
                                                style={{ minWidth: 200 }}
                                                value={bulkTemplateId}
                                                onChange={handleApplyTemplateToAll}
                                                loading={templatesLoading}
                                                allowClear
                                            >
                                                {contactTemplateOptions.map(opt => (
                                                    <Option key={opt.value} value={opt.value}>
                                                        {opt.label}
                                                    </Option>
                                                ))}
                                            </Select>
                                            <Tooltip title="将注册人信息同步到其他角色">
                                                <Button icon={<SyncOutlined />} onClick={syncContactsFromRegistrant}>
                                                    同步到其他联系人
                                                </Button>
                                            </Tooltip>
                                        </Space>
                                        <Form form={contactsForm} layout="vertical">
                                            <Row gutter={[16, 16]}>
                                                {CONTACT_ROLES.map(role => (
                                                    <Col xs={24} md={12} key={role.key}>
                                                        <Card
                                                            size="small"
                                                            title={role.label}
                                                            extra={
                                                                <Select
                                                                    placeholder="选择模板"
                                                                    value={roleTemplateIds[role.key]}
                                                                    style={{ minWidth: 140 }}
                                                                    onChange={(v) => handleRoleTemplateChange(role.key, v)}
                                                                    allowClear
                                                                >
                                                                    {contactTemplateOptions.map(opt => (
                                                                        <Option key={opt.value} value={opt.value}>
                                                                            {opt.label}
                                                                        </Option>
                                                                    ))}
                                                                </Select>
                                                            }
                                                        >
                                                            <Form.Item
                                                                label="名"
                                                                name={['contacts', role.key, 'firstName']}
                                                                rules={[{ required: true, message: '必填' }]}
                                                            >
                                                                <Input />
                                                            </Form.Item>
                                                            <Form.Item
                                                                label="姓"
                                                                name={['contacts', role.key, 'lastName']}
                                                                rules={[{ required: true, message: '必填' }]}
                                                            >
                                                                <Input />
                                                            </Form.Item>
                                                            <Form.Item
                                                                label="邮箱"
                                                                name={['contacts', role.key, 'email']}
                                                                rules={[{ required: true, message: '必填' }]}
                                                            >
                                                                <Input />
                                                            </Form.Item>
                                                            <Form.Item
                                                                label="电话"
                                                                name={['contacts', role.key, 'phone']}
                                                                rules={[{ required: true, message: '必填' }]}
                                                            >
                                                                <Input />
                                                            </Form.Item>
                                                            <Form.Item
                                                                label="地址"
                                                                name={['contacts', role.key, 'address']}
                                                                rules={[{ required: true, message: '必填' }]}
                                                            >
                                                                <Input />
                                                            </Form.Item>
                                                            <Form.Item
                                                                label="城市"
                                                                name={['contacts', role.key, 'city']}
                                                                rules={[{ required: true, message: '必填' }]}
                                                            >
                                                                <Input />
                                                            </Form.Item>
                                                            <Form.Item
                                                                label="州/省"
                                                                name={['contacts', role.key, 'state']}
                                                                rules={[{ required: true, message: '必填' }]}
                                                            >
                                                                <Input />
                                                            </Form.Item>
                                                            <Form.Item
                                                                label="邮编"
                                                                name={['contacts', role.key, 'postalCode']}
                                                                rules={[{ required: true, message: '必填' }]}
                                                            >
                                                                <Input />
                                                            </Form.Item>
                                                            <Form.Item
                                                                label="国家/地区"
                                                                name={['contacts', role.key, 'country']}
                                                                rules={[{ required: true, message: '必填' }]}
                                                            >
                                                                <CountryCodeInput placeholder="US" />
                                                            </Form.Item>
                                                        </Card>
                                                    </Col>
                                                ))}
                                            </Row>
                                            <Divider />
                                            <Button type="primary" onClick={handleContactsSubmit} loading={contactsSubmitting}>
                                                保存联系人
                                            </Button>
                                        </Form>
                                    </Space>
                                )
                            },
                            {
                                key: 'glue',
                                label: (
                                    <span>
                                        <CloudServerOutlined /> 胶水记录
                                    </span>
                                ),
                                children: (
                                    <Space direction="vertical" style={{ width: '100%', padding: 16 }} size={16}>
                                        <Alert
                                            message="胶水记录（Glue Records）"
                                            description="如果您使用此域名的子域名作为DNS服务器（如 ns1.yourdomain.com），需要添加胶水记录将主机名映射到IP地址。"
                                            type="info"
                                            showIcon
                                        />

                                        <Card type="inner" title="添加胶水记录">
                                            <Form form={glueForm} layout="inline">
                                                <Form.Item
                                                    name="hostname"
                                                    rules={[{ required: true, message: '请输入主机名' }]}
                                                >
                                                    <Input
                                                        placeholder={`ns1.${domain?.domain || 'example.com'}`}
                                                        style={{ width: 250 }}
                                                    />
                                                </Form.Item>
                                                <Form.Item
                                                    name="ipv4"
                                                    rules={[
                                                        { required: true, message: '请输入IPv4' },
                                                        { pattern: /^(\d{1,3}\.){3}\d{1,3}$/, message: 'IPv4格式不正确' }
                                                    ]}
                                                >
                                                    <Input placeholder="IPv4 地址" style={{ width: 150 }} />
                                                </Form.Item>
                                                <Form.Item name="ipv6">
                                                    <Input placeholder="IPv6 地址（可选）" style={{ width: 200 }} />
                                                </Form.Item>
                                                <Form.Item>
                                                    <Button
                                                        type="primary"
                                                        icon={<PlusOutlined />}
                                                        onClick={handleAddGlueRecord}
                                                        loading={glueSubmitting}
                                                    >
                                                        添加
                                                    </Button>
                                                </Form.Item>
                                            </Form>
                                        </Card>

                                        <Card
                                            type="inner"
                                            title="现有胶水记录"
                                            extra={
                                                <Button size="small" icon={<ReloadOutlined />} onClick={loadGlueRecords}>
                                                    刷新
                                                </Button>
                                            }
                                        >
                                            <Table
                                                size="small"
                                                columns={glueColumns}
                                                dataSource={glueRecords}
                                                rowKey="hostname"
                                                loading={glueLoading}
                                                pagination={false}
                                                locale={{ emptyText: '暂无胶水记录' }}
                                            />
                                        </Card>

                                        {/* Edit Glue Modal */}
                                        <Modal
                                            title={`编辑胶水记录: ${editingGlue?.hostname || ''}`}
                                            open={!!editingGlue}
                                            onCancel={() => setEditingGlue(null)}
                                            onOk={handleUpdateGlueRecord}
                                            confirmLoading={glueSubmitting}
                                        >
                                            <Form form={glueEditForm} layout="vertical">
                                                <Form.Item
                                                    label="IPv4 地址"
                                                    name="ipv4"
                                                    rules={[
                                                        { required: true, message: '请输入IPv4地址' },
                                                        { pattern: /^(\d{1,3}\.){3}\d{1,3}$/, message: 'IPv4格式不正确' }
                                                    ]}
                                                >
                                                    <Input placeholder="例如: 1.2.3.4" />
                                                </Form.Item>
                                                <Form.Item label="IPv6 地址（可选）" name="ipv6">
                                                    <Input placeholder="例如: 2001:db8::1" />
                                                </Form.Item>
                                            </Form>
                                        </Modal>
                                    </Space>
                                )
                            },
                            {
                                key: 'dnssec',
                                label: (
                                    <span>
                                        <SecurityScanOutlined /> DNSSEC
                                    </span>
                                ),
                                children: (
                                    <Space direction="vertical" style={{ width: '100%', padding: 16 }} size={16}>
                                        <Alert
                                            message="DNSSEC (DNS Security Extensions)"
                                            description="DNSSEC通过数字签名验证DNS响应的真实性，防止DNS欺骗攻击。添加DS记录到注册商后，需要在DNS服务器上配置相应的DNSKEY记录。"
                                            type="info"
                                            showIcon
                                        />

                                        {dnssecRecords.length >= 4 && (
                                            <Alert
                                                message="已达到最大DS记录数量"
                                                description="最多支持4条DS记录，请删除旧记录后再添加新记录。"
                                                type="warning"
                                                showIcon
                                            />
                                        )}

                                        <Card type="inner" title="添加DS记录">
                                            <Form form={dnssecForm} layout="vertical">
                                                <Row gutter={16}>
                                                    <Col span={6}>
                                                        <Form.Item
                                                            label="Key Tag"
                                                            name="key_tag"
                                                            rules={[
                                                                { required: true, message: '必填' },
                                                                {
                                                                    validator: (_, value) => {
                                                                        const num = parseInt(value);
                                                                        if (isNaN(num) || num < 0 || num > 65535) {
                                                                            return Promise.reject('0-65535');
                                                                        }
                                                                        return Promise.resolve();
                                                                    }
                                                                }
                                                            ]}
                                                        >
                                                            <Input placeholder="0-65535" />
                                                        </Form.Item>
                                                    </Col>
                                                    <Col span={6}>
                                                        <Form.Item
                                                            label="Algorithm"
                                                            name="algorithm"
                                                            rules={[{ required: true, message: '必填' }]}
                                                        >
                                                            <Select placeholder="选择算法">
                                                                {DNSSEC_ALGORITHMS.map(a => (
                                                                    <Option key={a.value} value={a.value}>{a.label}</Option>
                                                                ))}
                                                            </Select>
                                                        </Form.Item>
                                                    </Col>
                                                    <Col span={6}>
                                                        <Form.Item
                                                            label="Digest Type"
                                                            name="digest_type"
                                                            rules={[{ required: true, message: '必填' }]}
                                                        >
                                                            <Select placeholder="选择摘要类型">
                                                                {DNSSEC_DIGEST_TYPES.map(d => (
                                                                    <Option key={d.value} value={d.value}>{d.label}</Option>
                                                                ))}
                                                            </Select>
                                                        </Form.Item>
                                                    </Col>
                                                    <Col span={6}>
                                                        <Form.Item label=" ">
                                                            <Button
                                                                type="primary"
                                                                icon={<PlusOutlined />}
                                                                onClick={handleAddDnssec}
                                                                loading={dnssecSubmitting}
                                                                disabled={dnssecRecords.length >= 4}
                                                            >
                                                                添加
                                                            </Button>
                                                        </Form.Item>
                                                    </Col>
                                                </Row>
                                                <Form.Item
                                                    label="Digest (十六进制)"
                                                    name="digest"
                                                    rules={[
                                                        { required: true, message: '必填' },
                                                        { pattern: /^[0-9a-fA-F]+$/, message: '必须是十六进制' }
                                                    ]}
                                                >
                                                    <Input.TextArea
                                                        placeholder="输入十六进制格式的摘要值"
                                                        rows={2}
                                                    />
                                                </Form.Item>
                                            </Form>
                                        </Card>

                                        <Card
                                            type="inner"
                                            title="现有DS记录"
                                            extra={
                                                <Button size="small" icon={<ReloadOutlined />} onClick={loadDnssecRecords}>
                                                    刷新
                                                </Button>
                                            }
                                        >
                                            <Table
                                                size="small"
                                                columns={dnssecColumns}
                                                dataSource={dnssecRecords}
                                                rowKey={(_, index) => index}
                                                loading={dnssecLoading}
                                                pagination={false}
                                                locale={{ emptyText: '暂无DNSSEC记录' }}
                                                scroll={{ x: true }}
                                            />
                                        </Card>
                                    </Space>
                                )
                            },
                            {
                                key: 'sync-logs',
                                label: (
                                    <span>
                                        <HistoryOutlined /> 同步日志
                                    </span>
                                ),
                                children: (
                                    <Space direction="vertical" style={{ width: '100%', padding: 16 }} size={16}>
                                        <Alert
                                            message="注册商同步日志"
                                            description="展示该域名与注册商之间的同步历史（状态、锁定、隐私、到期时间等变更）。可手动触发一次同步以拉取最新状态。"
                                            type="info"
                                            showIcon
                                        />
                                        <Card
                                            type="inner"
                                            title="同步记录"
                                            extra={
                                                <Space>
                                                    <Button
                                                        type="primary"
                                                        icon={<SyncOutlined />}
                                                        onClick={handleTriggerSync}
                                                        loading={syncTriggering}
                                                    >
                                                        触发同步
                                                    </Button>
                                                    <Button
                                                        size="small"
                                                        icon={<ReloadOutlined />}
                                                        onClick={() => loadSyncLogs(syncLogsPage)}
                                                    >
                                                        刷新
                                                    </Button>
                                                </Space>
                                            }
                                        >
                                            <Table
                                                size="small"
                                                rowKey={(record, index) => record.id || index}
                                                columns={syncLogColumns}
                                                dataSource={syncLogs}
                                                loading={syncLogsLoading}
                                                scroll={{ x: true }}
                                                locale={{ emptyText: '暂无同步日志' }}
                                                pagination={{
                                                    current: syncLogsPage,
                                                    pageSize: 20,
                                                    total: syncLogsTotal,
                                                    showSizeChanger: false,
                                                    onChange: (page) => loadSyncLogs(page)
                                                }}
                                            />
                                        </Card>
                                    </Space>
                                )
                            }
                        ]}
                    />
                </Card>

                <Modal
                    title="转移码"
                    open={authCodeModalVisible}
                    onCancel={() => setAuthCodeModalVisible(false)}
                    footer={null}
                >
                    <Text copyable>{authCode || '暂无转移码'}</Text>
                </Modal>
            </Space>
        </Spin>
    );
};

export default DomainDetail;
